import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@chakra-ui/react';
import { verifyDriverPin } from '../../APIs';

const PIN_LENGTH = 4;
const FAIL_LIMIT = 5;
const LOCKOUT_SECS = 30;

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/**
 * Shared PIN input field used across driver authentication flows.
 * Handles digit entry, paste, shake animation, inline error display,
 * and brute-force lockout (5 wrong attempts → 30 s cooldown).
 *
 * @param {function} onVerified - Called with the verified driver object on success.
 * @param {string}   title      - Heading shown above the inputs.
 * @param {string}   subtitle   - Sub-heading shown below the title.
 * @param {string}   className  - Optional extra class for the wrapper.
 */
const PinInputField = ({
  onVerified,
  title = 'Enter Driver PIN',
  subtitle = 'Enter the fixed 4-digit PIN assigned to you.',
  className = '',
}) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [shake, setShake] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(0);
  const [countdown, setCountdown] = useState(0);

  const inputRefs = useRef([]);

  // Auto-focus first input on mount
  useEffect(() => {
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 180);
    return () => clearTimeout(t);
  }, []);

  // Countdown ticker while locked
  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(0);
        setCountdown(0);
        setFailCount(0);
        setErrorMsg('');
        clearInterval(interval);
        setTimeout(() => inputRefs.current[0]?.focus(), 80);
      } else {
        setCountdown(remaining);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 520);
  };

  const handleVerify = useCallback(() => {
    if (lockedUntil && Date.now() < lockedUntil) return;
    const pin = digits.join('');
    if (pin.length < PIN_LENGTH) {
      setErrorMsg('Please enter all 4 digits');
      triggerShake();
      return;
    }
    setLoading(true);
    setErrorMsg('');
    setHasError(false);

    verifyDriverPin({ pin }, (err, driver) => {
      setLoading(false);
      if (err || !driver) {
        const newCount = failCount + 1;
        setHasError(true);
        triggerShake();
        if (newCount >= FAIL_LIMIT) {
          const until = Date.now() + LOCKOUT_SECS * 1000;
          setLockedUntil(until);
          setCountdown(LOCKOUT_SECS);
          setErrorMsg(`Too many failed attempts. Try again in ${LOCKOUT_SECS}s.`);
        } else {
          const left = FAIL_LIMIT - newCount;
          setErrorMsg(`Invalid PIN. ${left} attempt${left !== 1 ? 's' : ''} remaining.`);
        }
        setFailCount(newCount);
        return;
      }
      setFailCount(0);
      onVerified(driver);
    });
  }, [digits, failCount, lockedUntil, onVerified]);

  const handleChange = useCallback((i, val) => {
    const cleaned = val.replace(/\D/g, '').slice(-1);
    setErrorMsg('');
    setHasError(false);
    setDigits(prev => { const next = [...prev]; next[i] = cleaned; return next; });
    if (cleaned && i < PIN_LENGTH - 1) inputRefs.current[i + 1]?.focus();
  }, []);

  const handleKeyDown = useCallback((i, e) => {
    if (lockedUntil && Date.now() < lockedUntil) return;
    if (e.key === 'Backspace') {
      if (digits[i]) {
        setDigits(prev => { const n = [...prev]; n[i] = ''; return n; });
      } else if (i > 0) {
        inputRefs.current[i - 1]?.focus();
        setDigits(prev => { const n = [...prev]; n[i - 1] = ''; return n; });
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      inputRefs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < PIN_LENGTH - 1) {
      inputRefs.current[i + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleVerify();
    }
  }, [digits, lockedUntil, handleVerify]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    const next = ['', '', '', ''];
    pasted.split('').forEach((ch, idx) => { if (idx < PIN_LENGTH) next[idx] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
    setErrorMsg('');
    setHasError(false);
  }, []);

  const clearAndRetry = () => {
    setDigits(['', '', '', '']);
    setErrorMsg('');
    setHasError(false);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  const isLocked = lockedUntil > 0 && Date.now() < lockedUntil;
  const pinFull = digits.every(d => d !== '');

  return (
    <div className={`df-pin-field-wrapper ${className}`.trim()}>
      <div className="df-pin-lock-icon"><LockIcon /></div>
      <p className="df-pin-title">{title}</p>
      <p className="df-pin-subtitle">{subtitle}</p>

      <div className={`df-pin-inputs-row${shake ? ' pin-shake' : ''}`} onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={el => inputRefs.current[i] = el}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={d}
            className={`df-pin-input-box${d ? ' pin-filled' : ''}${hasError ? ' pin-error-box' : ''}`}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            autoComplete="off"
            disabled={isLocked || loading}
          />
        ))}
      </div>

      <p className="df-pin-error-msg">
        {isLocked ? `Too many attempts — try again in ${countdown}s` : errorMsg}
      </p>

      {isLocked ? (
        <Button className="df-pin-verify-btn" isDisabled>
          LOCKED — {countdown}s
        </Button>
      ) : hasError ? (
        <Button className="df-pin-verify-btn df-pin-retry-btn" onClick={clearAndRetry}>
          CLEAR AND RETRY
        </Button>
      ) : (
        <Button
          className="df-pin-verify-btn"
          isDisabled={!pinFull || loading}
          isLoading={loading}
          loadingText="Verifying…"
          onClick={handleVerify}
        >
          VERIFY PIN
        </Button>
      )}
    </div>
  );
};

export default PinInputField;
